/** /content_search/ — Find & Replace across every notification-content source.
 *
 * Hand-authored wrapper (console convention: feature modules never depend on
 * generated types). `filters` is the QuerySearch wire AST (ContentFilterNode in
 * the OpenAPI spec).
 *
 * The load-bearing idea in this API is `search_key`: a search PINS its result
 * set server-side for a few minutes, and preview/replace both address that
 * pinned set rather than re-running the query. That is what makes "the rows you
 * approved are the rows that get written" true, and it is why an expired key is
 * a real, user-visible state rather than something to paper over.
 */
import type { WireNode } from '@/components/QuerySearch';

import { ConsoleApiError, apiGet, apiPost, extendedListConfig } from './client';

export interface ContentSnippet {
  before: string;
  match: string;
  after: string;
  offset: number;
}

export interface ContentSearchRow {
  /** `<kind>:<pk>:<path>` — the handle used in `selection.rows`. */
  id: string;
  kind: string;
  kind_label: string;
  pk: number;
  path: string;
  slot: string;
  slot_label: string;
  label: string;
  link: string | null;
  tenant: string | null;
  hits: number;
  snippets: ContentSnippet[];
  length: number;
  sha256: string;
  spans: number[][];
  truncated_spans: boolean;
  updated: string | null;
  /** False for identity fields — searchable, never replaceable. */
  replaceable: boolean;
  /** True when NO field of this kind can be replaced. */
  search_only: boolean;
}

export interface SkippedKind {
  kind: string;
  label: string;
  permission: string;
}

export interface ContentSearchResults {
  count: number;
  results: ContentSearchRow[];
  search_key: string;
  page: number;
  page_size: number;
  ordering: string;
  /** True when these rows came from the pinned set rather than a fresh run. */
  cached: boolean;
  /** When the pinned set was computed. */
  computed_at: string | null;
  capped: boolean;
  capped_kinds: string[];
  skipped_large: string[];
  timed_out: string[];
  per_kind_counts: Record<string, number>;
  /** Literal(s) pushed to SQL, including any derived from a regex. */
  prefilter_used: string[];
  kinds: string[];
  skipped_kinds: SkippedKind[];
}

export interface ContentSlotCatalog {
  scopes: {
    value: string;
    kinds: string[];
    labels: string[];
    permitted: boolean;
    replaceable: boolean;
  }[];
  kinds: {
    kind: string;
    label: string;
    search_only: boolean;
    view_permission: string;
    manage_permission: string | null;
    slots: { key: string; label: string; replaceable: boolean }[];
  }[];
}

export type ReplaceOutcomeCode =
  | 'will_change'
  | 'applied'
  | 'no_change'
  | 'stale'
  | 'missing'
  | 'rejected_not_replaceable'
  | 'rejected_too_long'
  | 'rejected_template_broken'
  | 'rejected_regex_timeout'
  | 'skipped_too_large'
  | 'failed';

export interface ReplaceHunk {
  offset: number;
  context_before: string;
  old: string;
  new: string;
  context_after: string;
}

export interface ReplaceOutcome {
  id: string;
  kind: string;
  kind_label: string | null;
  label: string;
  slot_label: string;
  /** Where this record is edited, so a diff can be opened in context. Null when
   *  the console has no page for the kind. */
  link: string | null;
  outcome: ReplaceOutcomeCode;
  detail: string;
  replacements: number;
  hunks: ReplaceHunk[];
  hunks_truncated: boolean;
  length_before: number;
  length_after: number;
}

export interface PreviewResults {
  total_selected: number;
  previewed: number;
  /** True when this is only the first N rows — a sample, not the dry run. */
  sampled: boolean;
  results: ReplaceOutcome[];
  summary: Partial<Record<ReplaceOutcomeCode, number>>;
}

export interface FindSpec {
  kind: 'literal' | 'regex';
  value: string;
  ignore_case?: boolean;
}

export interface ReplaceSelection {
  mode: 'rows' | 'all_matching';
  rows?: string[];
}

export interface ReplaceBody {
  search_key: string;
  selection: ReplaceSelection;
  /** Omit to replace the search's own matches. */
  find?: FindSpec;
  replacement: string;
  dry_run?: boolean;
  reason?: string;
}

export interface ReplaceDispatch {
  task_id: string;
  task_type: string;
  processed: number;
  dry_run: boolean;
  capped: boolean;
}

const BASE = '/content_search/';

/** The pinned result set aged out. Nothing is wrong with the query — the fix is
 *  mechanically to re-run it, which is why the backend gives it its own code
 *  rather than folding it into the generic filter error. */
export function isSearchExpired(e: unknown): boolean {
  return (
    e instanceof ConsoleApiError &&
    (e.body as { code?: string } | undefined)?.code === 'search_expired'
  );
}

export interface SearchParams {
  filters: WireNode[];
  ordering?: string;
  page?: number;
  page_size?: number;
  timeout?: 'extended';
  /** Re-run the search and overwrite the pinned set. A repeat request WITHOUT
   *  this returns the same rows for the whole server-side TTL, so any refresh
   *  affordance must send it or it is only refreshing the client cache. */
  refresh?: boolean;
}

export function search(p: SearchParams): Promise<ContentSearchResults> {
  const usp = new URLSearchParams();
  usp.set('filters', JSON.stringify(p.filters));
  if (p.ordering) usp.set('ordering', p.ordering);
  if (p.page) usp.set('page', String(p.page));
  if (p.page_size) usp.set('page_size', String(p.page_size));
  if (p.timeout) usp.set('timeout', p.timeout);
  if (p.refresh) usp.set('refresh', '1');
  return apiGet<ContentSearchResults>(`${BASE}?${usp.toString()}`, extendedListConfig(p.timeout));
}

export function slots(): Promise<ContentSlotCatalog> {
  return apiGet<ContentSlotCatalog>(`${BASE}slots/`);
}

export function preview(body: ReplaceBody): Promise<PreviewResults> {
  return apiPost<PreviewResults>(`${BASE}preview/`, body);
}

export function replace(body: ReplaceBody): Promise<ReplaceDispatch> {
  return apiPost<ReplaceDispatch>(`${BASE}replace/`, body);
}

/** Human labels for every outcome the backend can report. Kept exhaustive by
 *  the `ReplaceOutcomeCode` union — adding a code without a label is a type
 *  error, not a blank cell. */
export const OUTCOME_LABELS: Record<ReplaceOutcomeCode, string> = {
  will_change: 'Will change',
  applied: 'Changed',
  no_change: 'No change',
  stale: 'Changed since search',
  missing: 'No longer exists',
  rejected_not_replaceable: 'Not replaceable',
  rejected_too_long: 'Too long',
  rejected_template_broken: 'Would break the template',
  rejected_regex_timeout: 'Pattern too slow',
  skipped_too_large: 'Field too large',
  failed: 'Failed',
};

export const OUTCOME_SEVERITY: Record<ReplaceOutcomeCode, 'success' | 'info' | 'warning' | 'error'> =
  {
    will_change: 'success',
    applied: 'success',
    no_change: 'info',
    stale: 'warning',
    missing: 'warning',
    rejected_not_replaceable: 'warning',
    rejected_too_long: 'error',
    rejected_template_broken: 'error',
    rejected_regex_timeout: 'error',
    skipped_too_large: 'warning',
    failed: 'error',
  };
